import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import { authKeys } from "@/hooks/useAuth";

export function Signup() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!acceptedTerms) {
      setError("Please accept the terms to continue.");
      return;
    }
    setBusy(true);
    setError(null);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    });
    setBusy(false);
    if (signUpError) {
      setError(signUpError.message);
      return;
    }
    queryClient.setQueryData(authKeys.session(), data.session);
    navigate("/onboarding", { replace: true });
  }

  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center px-6"
      style={{ background: "#FFFCF8", color: "#1A1620" }}
    >
      <div className="w-full max-w-sm">
        <h1 className="font-display text-3xl" style={{ color: "#8A4E9C" }}>
          Create your garden
        </h1>
        <p className="mt-1 text-sm" style={{ color: "#9A8A78" }}>
          Sage starts learning your zone and goals after this.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <label className="block">
            <span className="text-sm font-medium">Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-base"
              style={{ border: "1px solid #EAE4DA", background: "#FFFFFF" }}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-base"
              style={{ border: "1px solid #EAE4DA", background: "#FFFFFF" }}
            />
            <span className="mt-1 block text-xs" style={{ color: "#9A8A78" }}>
              At least 8 characters.
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1"
            />
            <span style={{ color: "#1A1620" }}>
              I accept the terms of service and privacy policy.
            </span>
          </label>

          {error ? (
            <p
              role="alert"
              className="rounded-md px-3 py-2 text-sm"
              style={{ background: "#FBEDE6", color: "#A05A38" }}
            >
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-full px-5 py-3 text-base font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: "#8A4E9C", color: "#FFFFFF" }}
          >
            {busy ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="mt-8 text-center text-sm" style={{ color: "#9A8A78" }}>
          Already have an account?{" "}
          <Link to="/login" className="font-medium" style={{ color: "#8A4E9C" }}>
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
